import { DEFAULT_STEP_BOUNTY_POINTS } from './score';
import { TARGET_AT_LEAST_ONE } from '../recurrence/step-rules';

/**
 * Blank uses the step's own bar. A typed count has to be a whole number of at least 1,
 * the same sentence the server returns (`bountyStepViolation`).
 */
export function bountyTargetError(target: string): string | null {
  const trimmed = target.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) return TARGET_AT_LEAST_ONE;
  return null;
}

/** The non-series draft All-steps will publish, if there is one. Series rows are never it. */
export function draftGoingLive<T extends { status: string; seriesKey?: string | null }>(
  bounties: readonly T[]
): T | undefined {
  return bounties.find((bounty) => bounty.status === 'draft' && !bounty.seriesKey);
}

/**
 * Points a toggle will publish: that draft's payout, otherwise the shared default.
 * A live bounty is already on, so the confirm never quotes it.
 */
export function pointsGoingLive(
  bounties: ReadonlyArray<{ status: string; pointsReward: number; seriesKey?: string | null }>
): number {
  const draft = draftGoingLive(bounties);
  if (draft) return draft.pointsReward;
  return DEFAULT_STEP_BOUNTY_POINTS;
}

/**
 * Points the All-steps row shows. A live non-series bounty is what it pays now; otherwise
 * the draft that turning it on will publish; otherwise the default a new one is created at.
 * The "(5)" in a step's admin label is its count bar, not this number.
 */
export function pointsOnStep(
  bounties: ReadonlyArray<{ status: string; pointsReward: number; seriesKey?: string | null }>
): number {
  const outside = bounties.filter((bounty) => !bounty.seriesKey);
  const live = outside.find((bounty) => bounty.status === 'live');
  if (live) return live.pointsReward;
  return pointsGoingLive(outside);
}

/** "50 points" — the Points field and every payout sentence format the number here. */
export function payoutLabel(points: number): string {
  return `${points.toLocaleString()} points`;
}

/**
 * "1 member" / "12 members". Same one/other split as the guild and community catalogs
 * (`{count, plural, one {# member} other {# members}}`). The admin bounty pages are
 * English copy, so they call this instead of a catalog.
 */
export function memberCount(count: number): string {
  const label = count.toLocaleString();
  return count === 1 ? `${label} member` : `${label} members`;
}

/** How often the published bounty pays. A missing cadence is the new once bounty. */
function schedulePhrase(cadence: string | null | undefined): string {
  if (cadence === 'daily') return 'every day';
  if (cadence === 'weekly') return 'every week';
  if (cadence === 'monthly') return 'every month';
  return 'once';
}

/** Who the published bounty pays. A missing enrollment is the new everyone bounty. */
function whoCompletes(enrollment: string | null | undefined): string {
  if (enrollment === 'invite') return 'each invited member who completes';
  if (enrollment === 'optin') return 'each member who has joined and completes';
  return 'every member who completes';
}

/**
 * All-steps confirm. With no draft, the switch creates a once bounty open to everyone.
 * With a draft, it publishes that draft unchanged, so the sentence uses the draft's
 * schedule and who it pays.
 */
export function togglePaysLine(
  points: number,
  label: string,
  draft?: { cadence?: string | null; enrollment?: string | null } | null
): string {
  return `Pays ${payoutLabel(points)}, ${schedulePhrase(draft?.cadence)}, to ${whoCompletes(draft?.enrollment)} “${label}” from now on. Nothing done before now counts. The onboarding series is not affected.`;
}
